<template>
    <v-dialog
        v-model="showDialog"
        persistent
        eager
        :width="dialogWidth"
        @keydown.esc="onEscapeKeydown"
    >
        <v-card role="region" aria-label="data-export-dialog">
            <v-card-title class="py-2 px-4">
                <div>{{ title }}</div>
            </v-card-title>
            <v-card-text class="px-4 py-2">
                <v-container pa-0 fluid>
                    <v-row no-gutters class="align-center">
                        <v-col cols="9">
                            <div class="text-body-1">
                                <div>
                                    {{ displayMessage }}
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="3" class="text-right">
                            <v-img
                                contain
                                height="70px"
                                position="right"
                                :src="imageSource"
                            ></v-img>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
            <v-card-actions class="px-4 justify-end">
                <v-btn
                    outlined
                    class="mx-3"
                    small
                    :disabled="isDownloadLoading"
                    @click="onCloseClick"
                    >Close</v-btn
                >
                <download-excel
                    :fields="outputFields"
                    :data="exportData"
                    :name="outputFileName"
                    type="csv"
                >
                    <v-btn
                        color="primary"
                        small
                        :disabled="!isDownloadReady"
                        :loading="isDownloadLoading"
                        @click="onDownloadClick"
                        >Download File</v-btn
                    >
                </download-excel>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import JsonExcel from 'vue-json-excel';
import { isEmpty } from '@/common/helpers';

export default {
    name: 'CsvDataExporter',
    components: {
        'download-excel': JsonExcel
    },
    props: {
        title: {
            type: String,
            required: false,
            default: 'Export to CSV'
        },
        // the data entity name that will be exported ex: spaces, devices....
        entityName: {
            type: String,
            required: true
        },
        // the vuex action parameters to be send to the API
        dataSourceParams: {
            type: Object,
            required: true
        },
        // fields to be exported. { 'Name': 'user.name', 'Type': 'userType' }
        outputFields: {
            type: Object,
            required: true
        },
        // the download file name
        outputFileName: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            showDialog: true,
            defaultWidth: '400',
            displayMessage: 'Please wait while data is been exported...',
            imageSource: '/img/misc/spreadsheet1_640.png',
            isDownloadReady: false,
            isDownloadLoading: false,
            exportData: []
        };
    },
    computed: {
        dialogWidth() {
            if (this.$vuetify.breakpoint.smAndDown) {
                return '90%';
            } else {
                return this.defaultWidth;
            }
        }
    },
    mounted() {
        if (!this.isDownloadReady) {
            setTimeout(() => {
                this.startDataExport();
            }, 300);
        }
    },
    methods: {
        // ===================== Events ===================
        onCloseClick() {
            this.closeThisDialog();
        },
        onDownloadClick() {
            this.isDownloadReady = false;
            setTimeout(() => {
                this.closeThisDialog();
            }, 1000);
        },
        onEscapeKeydown() {
            this.onCloseClick();
        },
        // ===================== Others ===================
        closeThisDialog() {
            this.showDialog = false;
            this.$emit('close');
        },
        async startDataExport() {
            const somethingWrongMessage =
                'Something went wrong during the export process. Please close, and try it again!';
            try {
                this.isDownloadReady = false;
                this.isDownloadLoading = true;
                const data = await this.$store.dispatch('exportData', {
                    entityName: this.entityName,
                    ...this.dataSourceParams
                });
                if (!isEmpty(data)) {
                    this.exportData = data;
                    this.isDownloadReady = true;
                    this.displayMessage =
                        'Export file is ready. Click the download button below to save it.';
                } else {
                    this.displayMessage = somethingWrongMessage;
                }
            } catch (error) {
                this.displayMessage = somethingWrongMessage;
            } finally {
                // need to stop the screen loader
                this.isDownloadLoading = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped></style>
